import ExcelJS from "exceljs";

interface GameFormatRound {
  order: number;
  questionCount: number;
}

interface GameFormat {
  id: number;
  name: string;
  rounds: GameFormatRound[];
}

interface GameData {
  id: number;
  slug: string;
  name: string;
  format: GameFormat;
}

interface Team {
  key: number;
  name: string;
  answers: (boolean | null)[][];
}

// Convert 1-based column number to Excel letter(s): 1→A, 26→Z, 27→AA
const toRoman = (n: number): string => {
  const vals = [1000,900,500,400,100,90,50,40,10,9,5,4,1];
  const syms = ["M","CM","D","CD","C","XC","L","XL","X","IX","V","IV","I"];
  let result = "";
  for (let i = 0; i < vals.length; i++) {
    while (n >= vals[i]) { result += syms[i]; n -= vals[i]; }
  }
  return result;
};

const toCol = (n: number): string => {
  let result = "";
  while (n > 0) {
    result = String.fromCharCode(65 + ((n - 1) % 26)) + result;
    n = Math.floor((n - 1) / 26);
  }
  return result;
};

const FONT_SIZE = 15;

// ── Palette ──────────────────────────────────────────────────────────────────
const C = {
  headerBg:  "FF1D4ED8",  // deep blue  – header row background
  headerFg:  "FFFFFFFF",  // white      – header row text
  numColBg:  "FFDBEAFE",  // sky blue   – # column
  correctBg: "FFDCFCE7",  // mint green – correct answer cell (value === 1)
  dcColBg:   "FFE0E7FF",  // soft indigo – DC column
  ratingBg:  "FFFFF8E1",  // warm amber  – Reytinq column
  diffBg:    "FFFEF9C3",  // pale yellow – difficulty row
  rank1Bg:   "FFFFF3CD",  // champagne   – rank-1 row cells (lowest priority)
  altRowBg:  "FFF8FAFC",  // ice gray    – even-indexed data rows
  white:     "FFFFFFFF",
} as const;

function fill(argb: string): ExcelJS.Fill {
  return { type: "pattern", pattern: "solid", fgColor: { argb } };
}

// ── Formatting pass ───────────────────────────────────────────────────────────
// Applies font-size, alignment, and all fill colours in one pass.
//
// Priority (highest → lowest):
//   header row  >  difficulty row  >  correct-answer cell  >
//   # col  >  DC col  >  Reytinq col  >  rank-1 row  >  alt row  >  white
//
interface SheetConfig {
  nameColN:   number;   // column that holds the team name (left-aligned)
  numColN:    number;   // # column
  firstQColN: number;   // first question column (0 if no answer cols)
  lastQColN:  number;   // last  question column (0 if no answer cols)
  dcColN:     number;   // DC column
  ratingColN: number;   // Reytinq column
  diffRowN:   number;   // row index of the difficulty row (0 = none)
}

function applyFormatting(ws: ExcelJS.Worksheet, cfg: SheetConfig) {
  ws.eachRow((row, rowN) => {
    const isHeader   = rowN === 1;
    const isDiff     = cfg.diffRowN > 0 && rowN === cfg.diffRowN;
    const isRank1    = !isHeader && !isDiff && rowN === 2;
    const isAltRow   = !isHeader && !isDiff && rowN % 2 === 0; // even rows get alt

    row.eachCell({ includeEmpty: true }, (cell, colN) => {
      // ── Font
      cell.font = {
        size:  FONT_SIZE,
        bold:  isHeader || isDiff,
        color: isHeader ? { argb: C.headerFg } : undefined,
      };

      // ── Alignment
      cell.alignment = {
        horizontal: colN === cfg.nameColN ? "left" : "center",
        vertical:   "middle",
      };

      // ── Fill (priority order)
      if (isHeader) {
        cell.fill = fill(C.headerBg);
        return;
      }
      if (isDiff) {
        cell.fill = fill(C.diffBg);
        return;
      }

      const isAnswerCell =
        cfg.firstQColN > 0 &&
        colN >= cfg.firstQColN &&
        colN <= cfg.lastQColN &&
        cell.value === 1;

      if (isAnswerCell)           { cell.fill = fill(C.correctBg);  return; }
      if (colN === cfg.numColN)   { cell.fill = fill(C.numColBg);   return; }
      if (colN === cfg.dcColN)    { cell.fill = fill(C.dcColBg);    return; }
      if (colN === cfg.ratingColN){ cell.fill = fill(C.ratingBg);   return; }
      if (isRank1)                { cell.fill = fill(C.rank1Bg);    return; }
      if (isAltRow)               { cell.fill = fill(C.altRowBg);   return; }
      cell.fill = fill(C.white);
    });
  });
}

// ─────────────────────────────────────────────────────────────────────────────

export async function exportToExcel(game: GameData, sortedTeams: Team[]) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "3sual.az";
  wb.created = new Date();

  const nTeams = sortedTeams.length;

  // ── Column layout for round sheets (1-based) ─────────────────────────────
  //   1         → #
  //   2         → Komanda
  //   3..nQ+2   → Q1..Qn  (1 or null)
  //   nQ+3      → DC
  //   nQ+4      → Reytinq

  game.format.rounds.forEach((round, rIdx) => {
    const nQ         = round.questionCount;
    const firstQColN = 3;
    const lastQColN  = nQ + 2;
    const dcColN     = nQ + 3;
    const ratingColN = nQ + 4;
    const keyColN    = nQ + 5;
    const diffRowN   = nTeams + 2;

    const ws = wb.addWorksheet(`${toRoman(rIdx + 1)}`);

    // Header
    ws.addRow([
      "#",
      "Komanda",
      ...Array.from({ length: nQ }, (_, i) => i + 1),
      "DC",
      "Reytinq",
      "__KEY",
    ]);

    // Team rows
    sortedTeams.forEach((team, tIdx) => {
      const rowN = tIdx + 2;

      ws.addRow([
        tIdx + 1,
        team.name,
        ...Array.from({ length: nQ }, (_, qi) =>
          team.answers[rIdx]?.[qi] === true ? 1 : null
        ),
        null,
        null,
        team.key,
      ]);

      // DC
      const dcCorrect = (team.answers[rIdx] ?? []).filter((a) => a === true).length;
      ws.getRow(rowN).getCell(dcColN).value = {
        formula: `SUM(${toCol(firstQColN)}${rowN}:${toCol(lastQColN)}${rowN})`,
        result:  dcCorrect,
      };

      // Reytinq
      const roundRating = (team.answers[rIdx] ?? []).reduce((acc, ans, qi) => {
        if (ans !== true) return acc;
        const correct = sortedTeams.filter((t) => t.answers[rIdx]?.[qi] === true).length;
        return acc + (nTeams - correct);
      }, 0);
      ws.getRow(rowN).getCell(ratingColN).value = {
        formula: `SUMPRODUCT(${toCol(firstQColN)}${rowN}:${toCol(lastQColN)}${rowN},${toCol(firstQColN)}$${diffRowN}:${toCol(lastQColN)}$${diffRowN})`,
        result:  roundRating,
      };
    });

    // Difficulty row
    ws.addRow([null, "Çətinlik"]);
    for (let qi = 0; qi < nQ; qi++) {
      const colLetter = toCol(firstQColN + qi);
      const correct   = sortedTeams.filter((t) => t.answers[rIdx]?.[qi] === true).length;
      ws.getRow(diffRowN).getCell(firstQColN + qi).value = {
        formula: `${nTeams}-SUM(${colLetter}2:${colLetter}${nTeams + 1})`,
        result:  nTeams - correct,
      };
    }

    // Column widths
    ws.getColumn(1).width = 5;
    ws.getColumn(2).width = 24;
    for (let c = firstQColN; c <= lastQColN; c++) ws.getColumn(c).width = 6.5;
    ws.getColumn(dcColN).width = 7;
    ws.getColumn(ratingColN).width = 10;
    ws.getColumn(keyColN).hidden = true;

    // Row heights
    ws.getRow(1).height = 22;

    applyFormatting(ws, {
      nameColN: 2, numColN: 1,
      firstQColN, lastQColN, dcColN, ratingColN, diffRowN,
    });
  });

  // ── Yekun sheet ───────────────────────────────────────────────────────────
  //   1            → #
  //   2            → Komanda
  //   3..nRounds+2 → per-round DC
  //   nRounds+3    → DC (Cəmi)
  //   nRounds+4    → Reytinq

  const wsF         = wb.addWorksheet("Yekun");
  const nRounds     = game.format.rounds.length;
  const totalDcColN     = nRounds + 3;
  const totalRatingColN = nRounds + 4;
  const keyColN         = nRounds + 5;

  wsF.addRow([
    "#",
    "Komanda",
    ...game.format.rounds.map((_, rIdx) => `${toRoman(rIdx + 1)}`),
    "DC",
    "Reytinq",
    "__KEY",
  ]);

  // Pre-compute per-team totals so Nəticə can reuse them without recalculating
  const teamTotals = sortedTeams.map((team) => {
    const totalDC = game.format.rounds.reduce(
      (acc, _, rIdx) =>
        acc + (team.answers[rIdx] ?? []).filter((a) => a === true).length,
      0
    );
    const totalRating = game.format.rounds.reduce((acc, _, rIdx) => {
      return acc + (team.answers[rIdx] ?? []).reduce((rAcc, ans, qi) => {
        if (ans !== true) return rAcc;
        const correct = sortedTeams.filter((t) => t.answers[rIdx]?.[qi] === true).length;
        return rAcc + (nTeams - correct);
      }, 0);
    }, 0);
    return { totalDC, totalRating };
  });

  sortedTeams.forEach((team, tIdx) => {
    const rowN = tIdx + 2;
    const { totalDC, totalRating } = teamTotals[tIdx];

    wsF.getCell(rowN, 1).value = tIdx + 1;
    wsF.getCell(rowN, 2).value = team.name;

    // Per-round DC (cross-sheet refs; DC is now at nQ+3 in round sheets)
    game.format.rounds.forEach((round, rIdx) => {
      const roundDcColN = round.questionCount + 3;
      const roundDcVal  = (team.answers[rIdx] ?? []).filter((a) => a === true).length;
      wsF.getCell(rowN, rIdx + 3).value = {
        formula: `'${toRoman(rIdx + 1)}'!${toCol(roundDcColN)}${rowN}`,
        result:  roundDcVal,
      };
    });

    // Total DC
    wsF.getCell(rowN, totalDcColN).value = {
      formula: `SUM(${toCol(3)}${rowN}:${toCol(nRounds + 2)}${rowN})`,
      result:  totalDC,
    };

    // Total Reytinq (Reytinq is now at nQ+4 in round sheets)
    const roundRatingRefs = game.format.rounds
      .map((round, rIdx) => `'${toRoman(rIdx + 1)}'!${toCol(round.questionCount + 4)}${rowN}`)
      .join("+");
    wsF.getCell(rowN, totalRatingColN).value = {
      formula: roundRatingRefs,
      result:  totalRating,
    };
    wsF.getCell(rowN, keyColN).value = team.key;
  });

  wsF.getColumn(1).width = 5;
  wsF.getColumn(2).width = 24;
  for (let c = 3; c <= nRounds + 2; c++) wsF.getColumn(c).width = 6.5;
  wsF.getColumn(totalDcColN).width = 7;
  wsF.getColumn(totalRatingColN).width = 10;
  wsF.getColumn(keyColN).hidden = true;
  wsF.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: totalRatingColN } };
  wsF.getRow(1).height = 22;

  applyFormatting(wsF, {
    nameColN: 2, numColN: 1,
    firstQColN: 0, lastQColN: 0,   // no answer cols in Yekun
    dcColN: totalDcColN, ratingColN: totalRatingColN,
    diffRowN: 0,                    // no difficulty row in Yekun
  });

  // ── Nəticə sheet ─────────────────────────────────────────────────────────
  //   1 → #
  //   2 → Komanda
  //   3 → DC
  //   4 → Reytinq

  // ── Nəticə sheet ─────────────────────────────────────────────────────────
  //   1 → #
  //   2 → Komanda
  //   3 → DC       (INDEX/MATCH from Yekun by key — sort-safe)
  //   4 → Reytinq  (INDEX/MATCH from Yekun by key — sort-safe)
  //   5 → __KEY    (hidden)

  const wsN = wb.addWorksheet("Nəticə");
  const nKeyCol        = 5;
  const yekunDcRef     = `Yekun!${toCol(totalDcColN)}:${toCol(totalDcColN)}`;
  const yekunRatingRef = `Yekun!${toCol(totalRatingColN)}:${toCol(totalRatingColN)}`;
  const yekunKeyRef    = `Yekun!${toCol(keyColN)}:${toCol(keyColN)}`;

  wsN.addRow(["#", "Komanda", "DC", "Reytinq", "__KEY"]);

  sortedTeams.forEach((team, tIdx) => {
    const rowN   = tIdx + 2;
    const keyRef = `${toCol(nKeyCol)}${rowN}`;

    wsN.getCell(rowN, 1).value = tIdx + 1;
    wsN.getCell(rowN, 2).value = {                        
      formula: `INDEX(Yekun!B:B,MATCH(${keyRef},${yekunKeyRef},0))`,                                                
    };
    wsN.getCell(rowN, 3).value = {
      formula: `INDEX(${yekunDcRef},MATCH(${keyRef},${yekunKeyRef},0))`,
    };
    wsN.getCell(rowN, 4).value = {
      formula: `INDEX(${yekunRatingRef},MATCH(${keyRef},${yekunKeyRef},0))`,
    };
    wsN.getCell(rowN, nKeyCol).value = team.key;
  });

  wsN.getColumn(1).width = 5;
  wsN.getColumn(2).width = 24;
  wsN.getColumn(3).width = 7;
  wsN.getColumn(4).width = 10;
  wsN.getColumn(nKeyCol).hidden = true;
  wsN.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: nKeyCol } };
  wsN.getRow(1).height = 22;

  applyFormatting(wsN, {
    nameColN: 2, numColN: 1,
    firstQColN: 0, lastQColN: 0,
    dcColN: 3, ratingColN: 4,
    diffRowN: 0,
  });

  // ── Hidden metadata sheet for deterministic re-import mapping ────────────
  const meta = wb.addWorksheet("__3SUAL_META");
  meta.addRow(["schemaVersion", "1"]);
  meta.addRow(["slug", game.slug]);
  meta.addRow(["gameId", String(game.id)]);
  meta.addRow(["formatId", String(game.format.id)]);
  meta.addRow(["rounds", JSON.stringify(game.format.rounds.map(r => r.questionCount))]);
  meta.addRow(["exportedAt", new Date().toISOString()]);
  meta.state = "hidden";

  // ── Download ──────────────────────────────────────────────────────────────

  const buffer = await wb.xlsx.writeBuffer();
  const blob   = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a   = document.createElement("a");
  a.href     = url;
  a.download = `${game.name}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
